import { isFunction } from "lodash-es";

import type { Action } from "@/action/types";
import { captureMessage } from "@/error/sentry";
import { eventTarget, isInput } from "@/utils/dom/dom";

import type { Key, KeyAccept, SimpleKeyEvent } from "./key";
import { noAccept } from "./key";

/**
 * Register a shortcut that will never be unregistered.
 */
export function registerGlobalShortcut(
  action: Action<[], unknown> | AdHocAction,
  options?: ShortcutOptions,
) {
  registerShortcut("", action, options);
}

/**
 * Register a shortcut that can be unregistered via its component.
 */
export function registerShortcut(
  component: unknown,
  action: Action<[], unknown> | AdHocAction,
  options?: ShortcutOptions,
) {
  if (!isFunction(action)) {
    shortcuts.push({ component, accept: action.key.accept, exec: action.exec, options });
    return;
  }

  if (action.data.shortcut.accept === noAccept) {
    captureMessage("try to register action without shortcut", {
      info: { action: action.data.id },
    });
    return;
  }

  if (action.data.autoToggle) {
    options = {
      ...options,
      // we know when autoToggle is true, TParams must be [boolean?], see ActionDefinitionData
      up: () => (action as Action<[boolean?]>)("keyboard-shortcut", false),
    };
  }

  shortcuts.push({
    component,
    id: action.data.id,
    accept: action.data.shortcut.accept,
    exec: () => {
      if (!action.data.disabled?.()) {
        action("keyboard-shortcut");
      }
    },
    options,
  });
}

export function unregisterShortcuts(component: unknown) {
  shortcuts = shortcuts.filter((shortcut) => shortcut.component !== component);
}

export interface AdHocAction {
  key: Key;
  exec: (e: KeyboardEvent) => void;
}

export interface ShortcutOptions {
  prevent?: boolean;
  repeat?: boolean;
  up?: () => void;
  // if true, shortcut will work even when a modal is open. Defaults to false
  allowInModal?: boolean;
  // if true, shortcut will work even when a context menu is open. Defaults to false
  allowInContextMenu?: boolean;
}

interface Shortcut {
  id?: string;
  component: unknown;
  accept: KeyAccept;
  exec: (e: KeyboardEvent) => void;
  options?: ShortcutOptions;
}

let shortcuts = new Array<Shortcut>();
const down: Record<string, string> = {};
let modalOpen = false;
let contextMenuOpen = false;

export function setModalOpen(value: boolean) {
  modalOpen = value;
}

export function setContextMenuOpen(value: boolean) {
  contextMenuOpen = value;
}

document.addEventListener("keydown", keyDown);
document.addEventListener("keyup", keyUp);
window.addEventListener("blur", allKeysUp);

export function isKeyDown(codeOrKey: string) {
  return codeOrKey in down || Object.values(down).some((e) => e === codeOrKey);
}

function keyDown(e: KeyboardEvent) {
  const elem = eventTarget<HTMLInputElement>(e);

  const isActive = !elem.readOnly && !elem.disabled;
  const isAllowedInput =
    elem.classList.contains("allow-shortcuts") &&
    (e.altKey || e.ctrlKey || e.metaKey || e.key.length > 1) &&
    e.key !== "Backspace";

  const wasDown = e.code in down;
  down[e.code] = e.key;

  if (!isInput(elem) || !isActive || isAllowedInput) {
    if (e.key === " ") {
      // prevent scrolling
      e.preventDefault();
    }
    for (const cut of shortcuts) {
      if (cut.accept(e)) {
        if (modalOpen && !cut.options?.allowInModal) {
          continue;
        }
        if (contextMenuOpen && !cut.options?.allowInContextMenu) {
          continue;
        }
        if (cut.options?.prevent !== false) {
          e.preventDefault();
        }
        if (cut.options?.repeat || !wasDown) {
          cut.exec(e);
        }
      }
    }
  }
}

function allKeysUp() {
  for (const code in down) {
    keyUp({
      code,
      key: down[code],
      keyCode: 0,
      shiftKey: false,
      altKey: false,
      ctrlKey: false,
      metaKey: false,
    });
  }
}

function keyUp(e: SimpleKeyEvent) {
  delete down[e.code];
  for (const cut of shortcuts) {
    if (cut.accept(e)) {
      if (modalOpen && !cut.options?.allowInModal) {
        continue;
      }
      if (contextMenuOpen && !cut.options?.allowInContextMenu) {
        continue;
      }
      cut.options?.up?.();
    }
  }

  if (e.key === "Meta") {
    // on Mac, releasing any key together with command (=Meta), causes only a keyup event for Meta, but not the key
    // so assume that when releasing Meta every other key is also released
    allKeysUp();
  }
  if (e.key === "Escape") {
    // Adding this as a shortcut to reset the state of down[], because sometimes
    // the keyup event is not received (eg. on mac, after making a screenshot with cmd+shift+4)
    // which causes buggy behaviour since the app thinks some keys are still down
    allKeysUp();
  }
}
